import { useEffect, useMemo, useState } from 'react';
import { Box, Typography } from '@mui/material';
import ChevronLeftIcon from '@mui/icons-material/ChevronLeft';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import { Link as RouterLink, useNavigate } from 'react-router-dom';

import { LIST_PATH, PRINT_PATH, PrintButton } from './index';
import { DEFAULT_QUERY, printLabel, schoolOf, studentName, visibleRows, type ListQuery } from './listRows';
import { loadListState, saveListState, toggleSelected, type ListState } from './listState';
import { PrintSectionsDialog, sectionIds, usePrintSections } from './PrintSectionsDialog';
import { currentIdentity, onProgressReportsReset, progressReportSessionOrNone, type ProgressReportSession, type ProgressReportStudent, type ProgressReportStudents } from './students';
import { TextButton } from './TextButton';
import { LIST } from './tokens';

type StateStorage = Pick<Storage, 'getItem' | 'setItem' | 'removeItem'>;
type ListLoad = { kind: 'loading' } | { kind: 'ready'; data: ProgressReportStudents } | { kind: 'error' };
export type SessionSource = () => Promise<ProgressReportSession>;

export type PreviewStepper = { index: number; total: number; prev: ProgressReportStudent | null; next: ProgressReportStudent | null };

export function stepperFor(data: ProgressReportStudents, query: ListQuery, studentId: string): PreviewStepper | null {
  const own = data.students.find((student) => student.studentId === studentId);
  const school = query.school ?? (own ? schoolOf(own) : null);
  const rows = visibleRows(data.students, { ...query, school });
  const index = rows.findIndex((student) => student.studentId === studentId);
  if (index < 0) return null;
  return { index, total: rows.length, prev: rows[index - 1] ?? null, next: rows[index + 1] ?? null };
}

function NeighbourLink({ student, side }: { student: ProgressReportStudent; side: 'prev' | 'next' }) {
  const name = studentName(student);
  return (
    <Box
      component={RouterLink}
      to={`/reports/student-progress/${encodeURIComponent(student.studentId)}`}
      aria-label={name}
      sx={{
        display: 'inline-flex',
        alignItems: 'center',
        gap: '4px',
        color: LIST.primary,
        fontFamily: LIST.font,
        fontSize: 16,
        fontWeight: 600,
        lineHeight: '24px',
        textDecoration: 'none',
        whiteSpace: 'nowrap',
        '&:hover': { textDecoration: 'underline' },
      }}
    >
      {side === 'prev' && <ChevronLeftIcon sx={{ width: 20, height: 20 }} aria-hidden="true" />}
      {name}
      {side === 'next' && <ChevronRightIcon sx={{ width: 20, height: 20 }} aria-hidden="true" />}
    </Box>
  );
}

export type ReportNotice = { firstName: string; hasAccess: boolean };

export function noticeText({ firstName, hasAccess }: ReportNotice): string {
  const who = firstName || 'This student';
  return hasAccess
    ? `${who} hasn’t taken a Flashlight Benchmark yet, so this report is one page with ACCESS results only. You can still print it.`
    : `${who} hasn’t taken a Flashlight Benchmark yet and has no ACCESS results on file, so there is nothing to report yet.`;
}

export function NoBenchmarkNotice({ notice }: { notice: ReportNotice }) {
  return (
    <Box
      data-testid="no-benchmark-notice"
      role="note"
      sx={{
        display: 'flex',
        alignItems: 'flex-start',
        gap: '8px',
        px: '12px',
        py: '8px',
        borderRadius: '8px',
        bgcolor: LIST.notice.bg,
        color: LIST.notice.fg,
        fontFamily: LIST.font,
        fontSize: 16,
        lineHeight: '24px',
      }}
    >
      <InfoOutlinedIcon sx={{ width: 20, height: 20, mt: '2px', flexShrink: 0 }} aria-hidden="true" />
      {noticeText(notice)}
    </Box>
  );
}

const EMPTY_STATE: ListState = { query: DEFAULT_QUERY, selected: [] };

export function PreviewToolbar({
  studentId,
  title,
  subline,
  notice,
  listSource = progressReportSessionOrNone,
  storage,
  sectionsStorage,
  identity = currentIdentity,
}: {
  studentId: string | null;
  title: string;
  subline: string;
  notice: ReportNotice | null;
  listSource?: SessionSource;
  storage?: StateStorage | null;
  sectionsStorage?: StateStorage | null;
  identity?: () => Promise<string>;
}) {
  const navigate = useNavigate();
  const sections = usePrintSections(identity, sectionsStorage);
  const [list, setList] = useState<ListLoad>({ kind: 'loading' });
  const [state, setState] = useState<ListState>(EMPTY_STATE);
  const [generation, setGeneration] = useState(0);

  useEffect(
    () =>
      onProgressReportsReset(() => {
        setList({ kind: 'loading' });
        setState(EMPTY_STATE);
        setGeneration((n) => n + 1);
      }),
    [],
  );

  useEffect(() => {
    let cancelled = false;
    const settle = (next: ListLoad) => {
      if (cancelled) return;
      setList(next);
      setState((storage === undefined ? loadListState() : loadListState(storage)) ?? EMPTY_STATE);
    };
    listSource().then(
      (session) => settle(session.students === null ? { kind: 'error' } : { kind: 'ready', data: session.students }),
      () => settle({ kind: 'error' }),
    );
    return () => {
      cancelled = true;
    };
  }, [listSource, storage, generation]);

  const settled = list.kind !== 'loading';
  const listed = list.kind === 'ready' && studentId !== null && list.data.students.some((student) => student.studentId === studentId);
  const canToggle = settled && studentId !== null && listed;
  const selected = studentId !== null && state.selected.includes(studentId);
  const toggle = () => {
    if (!canToggle) return;
    const next = { ...state, selected: toggleSelected(state.selected, studentId) };
    setState(next);
    if (storage === undefined) saveListState(next);
    else saveListState(next, storage);
  };

  const stepper = useMemo(
    () => (list.kind === 'ready' && studentId !== null ? stepperFor(list.data, state.query, studentId) : null),
    [list, state.query, studentId],
  );

  return (
    <Box
      className="report-toolbar"
      sx={{
        bgcolor: '#FFFFFF',
        borderBottom: `1px solid ${LIST.divider}`,
        padding: '20px 24px 16px',
        display: 'flex',
        flexDirection: 'column',
        gap: '12px',
        fontFamily: LIST.font,
      }}
    >
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: '16px', flexWrap: 'wrap' }}>
        <TextButton
          label="Back to student list"
          onClick={() => navigate(LIST_PATH)}
          startIcon={<ChevronLeftIcon sx={{ width: 20, height: 20 }} aria-hidden="true" />}
        />
        {stepper !== null ? (
          <Box sx={{ display: 'flex', alignItems: 'center', gap: '16px' }}>
            {stepper.prev !== null && <NeighbourLink student={stepper.prev} side="prev" />}
            <Typography data-testid="stepper-position" sx={{ fontFamily: LIST.font, fontSize: 16, lineHeight: '24px', color: LIST.muted }}>
              {stepper.index + 1} of {stepper.total}
            </Typography>
            {stepper.next !== null && <NeighbourLink student={stepper.next} side="next" />}
          </Box>
        ) : (
          list.kind !== 'loading' && <Box data-testid="stepper-unavailable" />
        )}
      </Box>
      <Box sx={{ display: 'flex', alignItems: 'flex-end', justifyContent: 'space-between', gap: '16px' }}>
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: '2px', minWidth: 0 }}>
          <Typography component="h1" sx={{ fontFamily: LIST.font, fontSize: 28, lineHeight: '36px', fontWeight: 600, color: LIST.ink }}>
            {title}
          </Typography>
          <Typography sx={{ fontFamily: LIST.font, fontSize: 16, lineHeight: '24px', fontWeight: 500, color: LIST.muted, minHeight: 24 }}>
            {subline}
          </Typography>
        </Box>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: '12px', flexShrink: 0 }}>
          <Box
            component="label"
            sx={{
              display: 'inline-flex',
              alignItems: 'center',
              gap: '8px',
              height: 40,
              px: '12px',
              borderRadius: '8px',
              border: `1px solid ${LIST.primary}`,
              bgcolor: selected ? LIST.selectedBg : '#FFFFFF',
              color: LIST.ink,
              fontSize: 16,
              fontWeight: 600,
              lineHeight: '20px',
              cursor: canToggle ? 'pointer' : 'default',
              opacity: canToggle ? 1 : 0.6,
              whiteSpace: 'nowrap',
            }}
          >
            <Box
              component="input"
              type="checkbox"
              checked={selected}
              disabled={!canToggle}
              onChange={toggle}
              aria-label={selected ? 'Selected for printing' : 'Select for printing'}
              sx={{ width: 20, height: 20, m: 0, cursor: 'pointer', accentColor: LIST.primary }}
            />
            {selected ? 'Selected for printing' : 'Select for printing'}
          </Box>
          <PrintButton count={state.selected.length} onClick={sections.open} />
        </Box>
      </Box>
      {notice !== null && <NoBenchmarkNotice notice={notice} />}
      {sections.pending !== null && (
        <PrintSectionsDialog
          open
          title={printLabel(state.selected.length)}
          groupLabel="Include in each report"
          primaryLabel="Preview reports"
          chosen={sections.pending.chosen}
          onCancel={sections.cancel}
          onConfirm={(chosen) => {
            sections.save(chosen);
            navigate(PRINT_PATH, { state: { sections: sectionIds(chosen) } });
          }}
        />
      )}
    </Box>
  );
}
